import { Injectable } from '@angular/core';
import {
  EoViewComponentDescriptor,
  EoViewComponentTypeEnum,
  SeverityEnum,
} from 'ngx-eo-web-components';
import { Surgery } from '../../modules/core/models/surgery.model';

@Injectable({
  providedIn: 'root',
})
export class SurgeryViewService {
  private componentTypes: { [key: string]: EoViewComponentTypeEnum } = {
    uniqueListPatientCode: EoViewComponentTypeEnum.TEXT,
    surgeryCode: EoViewComponentTypeEnum.TEXT,
    assignmentDate: EoViewComponentTypeEnum.DATE,
    releaseDate: EoViewComponentTypeEnum.DATE,
    assignmentMonth: EoViewComponentTypeEnum.TEXT,
    hospitalName: EoViewComponentTypeEnum.TEXT,
    clinicName: EoViewComponentTypeEnum.TEXT,
    surgeryType: EoViewComponentTypeEnum.TEXT,
    listEntryDate: EoViewComponentTypeEnum.DATE,
    examinationDate: EoViewComponentTypeEnum.DATE,
    patientAmka: EoViewComponentTypeEnum.TEXT,
    surgeryDate: EoViewComponentTypeEnum.DATE,
    //idikaSurgeryIcds: EoViewComponentTypeEnum.TEXT,
    idikaSurgeryOperations: EoViewComponentTypeEnum.TEXT,
    surgeryStatusName: EoViewComponentTypeEnum.LABEL,
    cancelComments: EoViewComponentTypeEnum.TEXT,
    cancelReason: EoViewComponentTypeEnum.TEXT,
    entryTicketNumber: EoViewComponentTypeEnum.TEXT,
    drg: EoViewComponentTypeEnum.TEXT,
  };

  private labels: { [key: string]: string } = {
    uniqueListPatientCode: 'Μον. κωδικός ΕΛΧ',
    surgeryCode: 'Voucher',
    patientAmka: 'ΑΜΚΑ',
    assignmentDate: 'Ημ. ανάθεσης',
    releaseDate: 'Ημ. αποδέσμευσης για ιδιωτικές δομές',
    assignmentMonth: 'Μήνας ανάθεσης',
    hospitalName: 'Νοσοκομείο',
    clinicName: 'Κλινική',
    surgeryType: 'Τύπος',
    listEntryDate: 'Ημ. εισαγωγής στην ΕΛΧ',
    examinationDate: 'Ημ. κλινικής εκτίμησης',
    surgeryDate: 'Ημ. χειρουργείου',
    surgeryStatusName: 'Κατάσταση',
    //idikaSurgeryIcds: 'Λίστα ICDS',
    idikaSurgeryOperations: 'Λίστα χειρουργικών επεμβάσεων',
    entryTicketNumber: 'Αριθμός Εισιτηρίου',
    drg:'Drg'
  };

  private severities: { [key: string]: (surgery: Surgery) => string } = {
    surgeryStatusName: (surgery: Surgery) =>
      this.getSurgeryStatusSeverity(surgery),
  };

  private toolTipTexts: { [key: string]: (surgery: Surgery) => string } = {
    surgeryStatusName: (surgery: Surgery) =>
      this.getCanceledTooltipHtml(surgery),
  };

  private toolTipPositions: { [key: string]: string } = {
    surgeryStatusName: 'left',
  };

  generateViewComponents(surgery: Surgery, isViewOnly: boolean = false): EoViewComponentDescriptor[] {
    const allKeys = [
      'uniqueListPatientCode',
      'surgeryCode',
      'patientAmka',
      'assignmentDate',
      'releaseDate',
      'assignmentMonth',
      'hospitalName',
      'clinicName',
      'surgeryType',
      'listEntryDate',
      'examinationDate',
      'surgeryDate',
      'surgeryStatusName',
       //'idikaSurgeryIcds',
      'idikaSurgeryOperations',
    ];

    const viewOnlyExcluded = ['patientAmka', 'surgeryType'];
    const keys = isViewOnly
      ? allKeys.filter((k) => !viewOnlyExcluded.includes(k))
      : allKeys;

    return keys
      .filter((key) => surgery[key as keyof Surgery] !== undefined)
      .map((key) => ({
        componentType: this.getComponentTypeForKey(key),
        label: this.getLabelForKey(key),
        value: surgery[key as keyof Surgery],
        severity: this.getSeverity(key, surgery),
        toolTipText: this.getToolTipText(key, surgery),
        toolTipPosition: this.getToopTipPosition(key),
      }));
  }

  getSeverity(key: string, surgery: Surgery): string {
    if (!this.severities[key]) {
      return '';
    }
    return this.severities[key](surgery);
  }

  getToopTipPosition(key: string): string {
    if (!this.toolTipPositions[key]) {
      return '';
    }
    return this.toolTipPositions[key];
  }

  getToolTipText(key: string, surgery: Surgery): string {
    if (!this.toolTipTexts[key]) {
      return '';
    }
    return this.toolTipTexts[key](surgery);
  }

  getComponentTypeForKey(key: string): EoViewComponentTypeEnum {
    return this.componentTypes[key] || EoViewComponentTypeEnum.TEXT;
  }

  getLabelForKey(key: string): string {
    return this.labels[key] || key;
  }

  addViewComponents(
    viewComponents: EoViewComponentDescriptor[],
    newComponents: EoViewComponentDescriptor[]
  ): EoViewComponentDescriptor[] {
    return [...viewComponents, ...newComponents];
  }

  private getCanceledTooltipHtml(surgery: Surgery): string {
    if (surgery.surgeryStatusAlias != 'canceled') {
      return '';
    }
    return `<strong>Αιτιολογία</strong>: ${
      surgery?.cancelReason
    }<br/><strong>Σχόλιο</strong>: ${surgery?.cancelComments ?? '-'}`;
  }

  getSurgeryStatusSeverity(surgery: Surgery): string {
    switch (surgery.surgeryStatusAlias) {
      case 'canceled':
        return SeverityEnum.Danger;
      case 'completed':
        return SeverityEnum.Success;
      case 'assigned':
        return SeverityEnum.Warning;
      case 'scheduled':
        return SeverityEnum.Info;
      default:
        return '';
    }
  }
}
